import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';

import { Account } from '@ndvr/portfolio-api';

import { TooltipProvider } from '@/components/ui/tooltip';

import { useSuspendedAccounts } from '../portfolio/account-queries';
import { DashboardCard } from './DashboardCard';

const columnHelper = createColumnHelper<Account>();

const columns = [
    columnHelper.accessor((row) => row.accountName?.plainText, {
        id: 'account',
        header: 'Account',
        cell: (info) => <span className="flex items-center gap-2 font-medium">{info.getValue()}</span>,
    }),
    columnHelper.accessor('tradingSuspendedAt', {
        id: 'suspended',
        header: 'Trading Disabled',
        cell: (info) => <span className="text-gray-600">{new Date(info.getValue()!).toLocaleDateString()}</span>,
    }),
];

export const SuspendedAccounts = () => {
    const { data: suspendedAccounts = [], isPending } = useSuspendedAccounts();

    const sortedAccounts = useMemo(
        () =>
            [...suspendedAccounts].sort((a, b) => {
                const aTime = a.tradingSuspendedAt ? new Date(a.tradingSuspendedAt).getTime() : 0;
                const bTime = b.tradingSuspendedAt ? new Date(b.tradingSuspendedAt).getTime() : 0;
                return bTime - aTime; // newest first
            }),
        [suspendedAccounts],
    );

    const table = useReactTable({
        data: sortedAccounts,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <DashboardCard title={<>Accounts with Trading Disabled</>} size="third">
            {isPending ? (
                <div className="text-gray-500">Loading...</div>
            ) : sortedAccounts.length === 0 ? (
                <div className="text-gray-500">No accounts with trading disabled</div>
            ) : (
                <TooltipProvider delayDuration={0}>
                    <div className="relative">
                        <div className="max-h-[320px] overflow-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <tr key={headerGroup.id} className="border-b">
                                            {headerGroup.headers.map((header) => (
                                                <th key={header.id} className="p-2 text-left">
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column.columnDef.header,
                                                              header.getContext(),
                                                          )}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map((row) => (
                                        <tr key={row.id} className="border-b last:border-b-0">
                                            {row.getVisibleCells().map((cell) => (
                                                <td key={cell.id} className="p-2">
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-4 bg-gradient-to-t from-white to-transparent"></div>
                    </div>
                </TooltipProvider>
            )}
        </DashboardCard>
    );
};
